import { useState, useEffect, useMemo } from 'react';
import { Calendar, Clock, MapPin, Search, ChevronLeft, ChevronRight, ExternalLink, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { GoogleEvent } from '../types/calendar.types';
import { useGoogleCalendarStore } from '../store/useGoogleCalendarStore';
import { format, startOfDay, endOfDay, addDays, subDays } from 'date-fns';
import { it } from 'date-fns/locale';

// Intervallo (timeMin/timeMax) da chiedere a Google per la vista corrente,
// con un margine di 7 giorni così la settimana/mese a cavallo è sempre coperta.
function rangeForView(date: Date, view: 'day' | 'week' | 'month') {
  if (view === 'day') {
    return { min: startOfDay(subDays(date, 1)), max: endOfDay(addDays(date, 1)) };
  }
  if (view === 'week') {
    const start = startOfDay(subDays(date, date.getDay()));
    return { min: subDays(start, 7), max: endOfDay(addDays(start, 13)) };
  }
  const monthStart = startOfDay(new Date(date.getFullYear(), date.getMonth(), 1));
  const monthEnd = endOfDay(new Date(date.getFullYear(), date.getMonth() + 1, 0));
  return { min: subDays(monthStart, 7), max: addDays(monthEnd, 7) };
}

export default function CalendarPage() {
  const { events, loading, error, configured, fetchEvents, getEventsByDate, clearError } = useGoogleCalendarStore();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'day' | 'week' | 'month'>('week');

  // Ricarica gli eventi quando cambia la vista o la data selezionata.
  useEffect(() => {
    const { min, max } = rangeForView(selectedDate, viewMode);
    fetchEvents(min, max);
  }, [selectedDate, viewMode, fetchEvents]);

  const reload = () => {
    const { min, max } = rangeForView(selectedDate, viewMode);
    fetchEvents(min, max);
  };

  const getEventsForWeek = (date: Date) => {
    const start = startOfDay(subDays(date, date.getDay()));
    const end = endOfDay(addDays(start, 6));
    return events.filter((ev) => {
      const d = new Date(ev.startDate);
      return d >= start && d <= end;
    });
  };

  const getEventsForMonth = (date: Date) => {
    const start = startOfDay(new Date(date.getFullYear(), date.getMonth(), 1));
    const end = endOfDay(new Date(date.getFullYear(), date.getMonth() + 1, 0));
    return events.filter((ev) => {
      const d = new Date(ev.startDate);
      return d >= start && d <= end;
    });
  };

  const formatTime = (ev: GoogleEvent) => {
    if (ev.allDay) return 'Tutto il giorno';
    const start = format(new Date(ev.startDate), 'HH:mm', { locale: it });
    const end = format(new Date(ev.endDate), 'HH:mm', { locale: it });
    return `${start} → ${end}`;
  };

  const renderEventCard = (ev: GoogleEvent) => {
   const isLead = ev.id.startsWith('lead:');
   return (
    <Card key={ev.id} className={`mb-3 hover:shadow-md transition-shadow ${isLead ? 'border-l-4 border-l-indigo-500' : ''}`}>
      <CardContent className="p-4 md:p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className={`w-4 h-4 flex-shrink-0 ${isLead ? 'text-indigo-600' : 'text-blue-600'}`} />
              <h3 className="font-semibold text-sm truncate">{ev.title}</h3>
              {isLead && <Badge className="text-xs bg-indigo-100 text-indigo-800 border-indigo-200">Lead</Badge>}
              {ev.allDay && <Badge className="text-xs bg-purple-100 text-purple-800 border-purple-200">Giornata</Badge>}
            </div>

            <div className="flex items-center gap-4 text-xs text-gray-500 mb-2 flex-wrap">
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                <span className="font-semibold text-gray-700">
                  {format(new Date(ev.startDate), 'dd MMM yyyy', { locale: it })} · {formatTime(ev)}
                </span>
              </div>
              {ev.location && (
                <div className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  <span className="truncate">{ev.location}</span>
                </div>
              )}
            </div>

            {ev.description && <p className="text-sm text-gray-600 line-clamp-3 whitespace-pre-wrap">{ev.description}</p>}
          </div>

          {ev.htmlLink && (
            <a href={ev.htmlLink} target="_blank" rel="noreferrer" title={isLead ? 'Apri lead' : 'Apri su Google Calendar'}>
              <Button size="sm" variant="ghost" className="p-1 h-6 w-6 hover:bg-blue-50">
                <ExternalLink className={`w-3 h-3 ${isLead ? 'text-indigo-600' : 'text-blue-600'}`} />
              </Button>
            </a>
          )}
        </div>
      </CardContent>
    </Card>
   );
  };

  const daySelectedEvents = getEventsByDate(selectedDate);

  const viewEvents = useMemo(() => {
    let evs: GoogleEvent[];
    switch (viewMode) {
      case 'day': evs = getEventsByDate(selectedDate); break;
      case 'month': evs = getEventsForMonth(selectedDate); break;
      default: evs = getEventsForWeek(selectedDate);
    }
    if (!searchTerm) return evs;
    const term = searchTerm.toLowerCase();
    return evs.filter((ev) =>
      (ev.title?.toLowerCase() || '').includes(term) ||
      (ev.location?.toLowerCase() || '').includes(term) ||
      (ev.description?.toLowerCase() || '').includes(term)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, viewMode, selectedDate, searchTerm]);

  const navigatePrev = () => {
    setSelectedDate((prev) => {
      const next = new Date(prev);
      if (viewMode === 'day') next.setDate(prev.getDate() - 1);
      if (viewMode === 'week') next.setDate(prev.getDate() - 7);
      if (viewMode === 'month') next.setMonth(prev.getMonth() - 1);
      return next;
    });
  };

  const navigateNext = () => {
    setSelectedDate((prev) => {
      const next = new Date(prev);
      if (viewMode === 'day') next.setDate(prev.getDate() + 1);
      if (viewMode === 'week') next.setDate(prev.getDate() + 7);
      if (viewMode === 'month') next.setMonth(prev.getMonth() + 1);
      return next;
    });
  };

  const viewTitle = () => {
    if (viewMode === 'day') return format(selectedDate, 'EEEE dd MMMM yyyy', { locale: it });
    if (viewMode === 'week') {
      const start = startOfDay(subDays(selectedDate, selectedDate.getDay()));
      const end = endOfDay(addDays(start, 6));
      return `${format(start, 'dd MMM', { locale: it })} - ${format(end, 'dd MMM yyyy', { locale: it })}`;
    }
    return format(selectedDate, 'MMMM yyyy', { locale: it });
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Calendario</h1>
          <p className="text-sm text-gray-600 mt-1">Google Calendar + prossime azioni lead</p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={reload} disabled={loading} className="flex-1 sm:flex-none">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aggiorna
          </Button>
          <a href="https://calendar.google.com" target="_blank" rel="noreferrer" className="flex-1 sm:flex-none">
            <Button className="w-full">
              <ExternalLink className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Apri Google Calendar</span>
              <span className="sm:hidden">Google</span>
            </Button>
          </a>
        </div>
      </div>

      {/* Google non configurato */}
      {!configured && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-amber-800">Google Calendar non collegato</h3>
          <p className="text-sm text-amber-700 mt-1">
            Le credenziali Google non sono ancora impostate sul server. Una volta collegato l'account,
            gli eventi compariranno qui automaticamente.
          </p>
        </div>
      )}

      {/* Errore */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="text-sm font-medium text-red-800">Errore</h3>
            <p className="text-sm text-red-700 mt-1 break-words">{error}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={clearError} className="text-red-400 hover:text-red-600">✕</Button>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          placeholder="Cerca eventi, luoghi o note..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Navigazione + vista */}
      <div className="space-y-2 sm:space-y-0 sm:flex sm:items-center sm:gap-2 sm:flex-wrap">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={navigatePrev} className="flex-shrink-0">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1 px-2 text-sm text-gray-700 text-center sm:flex-none sm:min-w-[180px]">{viewTitle()}</div>
          <Button variant="outline" size="icon" onClick={navigateNext} className="flex-shrink-0">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        <div className="hidden sm:block w-px h-6 bg-gray-200 mx-2" />
        <div className="grid grid-cols-4 gap-2 sm:flex sm:items-center sm:gap-2">
          <Button variant={viewMode === 'day' ? 'default' : 'outline'} size="sm" onClick={() => setViewMode('day')}>Giorno</Button>
          <Button variant={viewMode === 'week' ? 'default' : 'outline'} size="sm" onClick={() => setViewMode('week')}>Settimana</Button>
          <Button variant={viewMode === 'month' ? 'default' : 'outline'} size="sm" onClick={() => setViewMode('month')}>Mese</Button>
          <Button variant="ghost" size="sm" onClick={() => setSelectedDate(new Date())}>Oggi</Button>
        </div>
      </div>

      {/* Contenuto */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Giorno selezionato */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Calendar className="w-4 h-4 text-yellow-600" />
                {format(selectedDate, 'EEEE dd MMM', { locale: it })}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {daySelectedEvents.length === 0 ? (
                <div className="text-center py-6">
                  <Calendar className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">Nessun evento</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {daySelectedEvents.map((ev) => (
                    <div key={ev.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                      <div className={`p-1.5 rounded ${ev.id.startsWith('lead:') ? 'bg-indigo-100 text-indigo-800' : 'bg-blue-100 text-blue-800'}`}>
                        <Calendar className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-sm text-gray-900 truncate">{ev.title}</h4>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <Clock className="w-3 h-3" />
                          {ev.allDay ? 'Giornata' : format(new Date(ev.startDate), 'HH:mm', { locale: it })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Eventi della vista */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg md:text-2xl">
                {viewMode === 'day' && 'Eventi del giorno'}
                {viewMode === 'week' && 'Eventi della settimana'}
                {viewMode === 'month' && 'Eventi del mese'}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {loading && events.length === 0 ? (
                <div className="flex items-center justify-center py-8 gap-2">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                  <span className="text-gray-600">Caricamento eventi...</span>
                </div>
              ) : viewEvents.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  {searchTerm ? 'Nessun evento trovato' : 'Nessun evento in programma'}
                </p>
              ) : (
                <div className="space-y-3">{viewEvents.map(renderEventCard)}</div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
